import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Edit3, Plus, X } from "lucide-react";
import { grossToNet, netToGross, formatMKD, RATES } from "@/lib/mk-salary";
import { type PayrollEntry } from "@/lib/api";
import { ADJ_TYPES } from "./payroll-constants";

function EntrySheet({
  entry,
  open,
  onClose,
  isDraft,
  onUpdateEntry,
  onAddAdjustment,
  onDeleteAdjustment,
  readOnly = false,
  t,
}: {
  entry: PayrollEntry | null;
  open: boolean;
  onClose: () => void;
  isDraft: boolean;
  onUpdateEntry: (
    entry: PayrollEntry,
    amount: number,
    inputType: "gross" | "net",
  ) => void;
  onAddAdjustment: (
    entryId: number,
    data: {
      type: string;
      name: string;
      amount: number;
      applyOn: string;
      notes?: string;
    },
  ) => void;
  onDeleteAdjustment: (entryId: number, adjId: number) => void;
  readOnly?: boolean;
  t: any;
}) {
  const [editValue, setEditValue] = useState("");
  const [editType, setEditType] = useState<"gross" | "net">("gross");
  const [isEditingSalary, setIsEditingSalary] = useState(false);
  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjForm, setAdjForm] = useState({
    type: "bonus",
    name: "",
    amount: "",
    applyOn: "gross",
    notes: "",
  });

  useEffect(() => {
    if (entry) {
      setEditValue(entry.grossSalary.toString());
      setEditType("gross");
      setIsEditingSalary(false);
      setShowAdjForm(false);
      setAdjForm({ type: "bonus", name: "", amount: "", applyOn: "gross", notes: "" });
    }
  }, [entry]);

  // Live salary preview — recomputes instantly as the user types
  const salaryPreview = useMemo(() => {
    if (!entry) return null;

    // Salary-edit preview
    if (isEditingSalary) {
      const val = parseInt(editValue);
      if (!isNaN(val) && val > 0) {
        const bd = editType === "gross" ? grossToNet(val) : netToGross(val);
        return {
          breakdown: bd,
          grossAdj: entry.totalAdjustmentsGross,
          netAdj: entry.totalAdjustmentsNet,
          finalNet: bd.netSalary + entry.totalAdjustmentsNet,
          isPreview: true,
        };
      }
    }

    // Adjustment-form preview
    if (showAdjForm) {
      const amt = parseInt(adjForm.amount);
      if (!isNaN(amt) && amt > 0) {
        const isPositive = adjForm.type === "bonus" || adjForm.type === "on_call";
        const signed = isPositive ? amt : -amt;
        const grossAdj =
          adjForm.applyOn === "gross"
            ? entry.totalAdjustmentsGross + signed
            : entry.totalAdjustmentsGross;
        const netAdj =
          adjForm.applyOn === "net"
            ? entry.totalAdjustmentsNet + signed
            : entry.totalAdjustmentsNet;
        const effectiveGross = entry.grossSalary + grossAdj;
        const bd = grossToNet(Math.max(1, effectiveGross));
        return {
          breakdown: bd,
          grossAdj,
          netAdj,
          finalNet: bd.netSalary + netAdj,
          isPreview: true,
        };
      }
    }

    return null;
  }, [entry, isEditingSalary, editValue, editType, showAdjForm, adjForm]);

  if (!entry) return null;

  const adjustments = entry.adjustments || [];
  const hasAdj = entry.totalAdjustmentsGross !== 0 || entry.totalAdjustmentsNet !== 0;

  // When gross adjustments are saved, recompute the full breakdown from the effective gross
  // so the breakdown table reflects the real (post-adjustment) deductions, not just the base.
  const savedBreakdown = hasAdj && entry.totalAdjustmentsGross !== 0
    ? grossToNet(Math.max(1, entry.grossSalary + entry.totalAdjustmentsGross))
    : null;

  // Decide what to show in the summary bar and breakdown
  // Priority: live preview > saved effective breakdown > raw entry fields
  const displayBreakdown = salaryPreview?.breakdown ?? savedBreakdown ?? null;
  const displayGross = displayBreakdown?.grossSalary ?? entry.grossSalary;
  const displayNet = salaryPreview
    ? salaryPreview.finalNet
    : (entry.finalNetSalary || entry.netSalary);

  const handleSaveSalary = () => {
    const val = parseInt(editValue);
    if (isNaN(val) || val <= 0) return;
    onUpdateEntry(entry, val, editType);
    setIsEditingSalary(false);
  };

  const handleAddAdj = () => {
    if (!adjForm.name || !adjForm.amount || parseInt(adjForm.amount) <= 0) return;
    onAddAdjustment(entry.id, {
      type: adjForm.type,
      name: adjForm.name,
      amount: parseInt(adjForm.amount),
      applyOn: adjForm.applyOn,
      notes: adjForm.notes || undefined,
    });
    setShowAdjForm(false);
    setAdjForm({ type: "bonus", name: "", amount: "", applyOn: "gross", notes: "" });
  };

  const breakdownRows = [
    { label: t("payroll.gross"), value: (displayBreakdown ? displayBreakdown.grossSalary : entry.grossSalary), bold: true, pct: "" },
    { label: t("payroll.pension") + ` (${(RATES.pension * 100).toFixed(1)}%)`, value: (displayBreakdown ? displayBreakdown.pensionContribution : entry.pensionContribution), pct: `${(RATES.pension * 100).toFixed(1)}%` },
    { label: t("payroll.health") + ` (${(RATES.health * 100).toFixed(1)}%)`, value: (displayBreakdown ? displayBreakdown.healthContribution : entry.healthContribution), pct: `${(RATES.health * 100).toFixed(1)}%` },
    { label: t("payroll.unemployment") + ` (${(RATES.unemployment * 100).toFixed(1)}%)`, value: (displayBreakdown ? displayBreakdown.unemploymentContribution : entry.unemploymentContribution), pct: `${(RATES.unemployment * 100).toFixed(1)}%` },
    { label: t("payroll.sickness") + ` (${(RATES.sickness * 100).toFixed(1)}%)`, value: (displayBreakdown ? displayBreakdown.sicknessContribution : entry.sicknessContribution), pct: `${(RATES.sickness * 100).toFixed(1)}%` },
    { label: t("payroll.contributions"), value: (displayBreakdown ? displayBreakdown.totalContributions : entry.totalContributions), bold: true, pct: "" },
    { label: t("payroll.grossMinusContributions"), value: (displayBreakdown ? displayBreakdown.grossMinusContributions : entry.grossMinusContributions), pct: "" },
    { label: t("payroll.personalExemption"), value: (displayBreakdown ? displayBreakdown.personalExemption : entry.personalExemption), pct: "" },
    { label: t("payroll.taxBase"), value: (displayBreakdown ? displayBreakdown.taxBase : entry.taxBase), pct: "" },
    { label: t("payroll.incomeTax"), value: (displayBreakdown ? displayBreakdown.personalIncomeTax : entry.personalIncomeTax), pct: "10%" },
    { label: t("payroll.deductions"), value: (displayBreakdown ? displayBreakdown.totalDeductions : entry.totalDeductions), bold: true, pct: "" },
    { label: t("payroll.net"), value: (displayBreakdown ? displayBreakdown.netSalary : entry.netSalary), bold: true, highlight: true, pct: "" },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <EmployeeAvatar
              avatarUrl={entry.avatarUrl}
              avatarColor={entry.avatarColor || "from-blue-400 to-blue-600"}
              initials={entry.employeeInitials || "?"}
              size="lg"
              className="shadow-md h-12 w-12"
            />
            <div>
              <DialogTitle>{entry.employeeName}</DialogTitle>
              <DialogDescription>
                {entry.jobTitle} &bull; {entry.department}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-2 space-y-5">
          {/* Summary bar — updates live */}
          <div className={`flex items-center justify-between p-3 rounded-lg ${salaryPreview ? "bg-primary/5 border border-primary/20" : "bg-muted/50"}`}>
            <div>
              <p className="text-xs text-muted-foreground">
                {t("payroll.gross")}{salaryPreview ? ` (${t("payroll.preview")})` : ""}
              </p>
              <p className={`text-lg font-bold tabular-nums ${salaryPreview && displayGross !== entry.grossSalary ? "text-primary" : ""}`}>
                {formatMKD(displayGross)} MKD
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">
                {(hasAdj || salaryPreview) ? t("payroll.finalNet") : t("payroll.net")}
                {salaryPreview ? ` (${t("payroll.preview")})` : ""}
              </p>
              <p className={`text-lg font-bold tabular-nums ${salaryPreview ? "text-primary" : "text-primary"}`}>
                {formatMKD(displayNet)} MKD
              </p>
            </div>
          </div>

          {isDraft && !readOnly && (
            <div>
              {isEditingSalary ? (
                <div className="p-3 rounded-lg border border-primary/20 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                  <p className="text-xs font-semibold text-muted-foreground">
                    {t("payroll.editSalary")}
                  </p>
                  <div className="flex items-center gap-2">
                    <select
                      value={editType}
                      onChange={(e) => setEditType(e.target.value as "gross" | "net")}
                      className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
                    >
                      <option value="gross">{t("payroll.inputGross")}</option>
                      <option value="net">{t("payroll.inputNet")}</option>
                    </select>
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSaveSalary()}
                      className="h-9 flex-1 rounded-lg border border-input bg-background px-3 text-sm tabular-nums"
                      autoFocus
                    />
                    <span className="text-xs text-muted-foreground">MKD</span>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setIsEditingSalary(false)}>
                      {t("common.cancel")}
                    </Button>
                    <Button size="sm" onClick={handleSaveSalary}>
                      {t("common.save")}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  onClick={() => {
                    setIsEditingSalary(true);
                    setEditValue(entry.grossSalary.toString());
                  }}
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  {t("payroll.editSalary")}
                </Button>
              )}
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              {t("payroll.breakdown")}
              {salaryPreview && (
                <span className="ml-2 text-primary font-normal">({t("payroll.preview")})</span>
              )}
            </p>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {breakdownRows.map((row, i) => (
                    <tr
                      key={i}
                      className={`border-b last:border-0 ${row.highlight ? "bg-primary/5" : i % 2 === 0 ? "bg-muted/30" : ""}`}
                    >
                      <td
                        className={`px-4 py-2.5 ${row.bold ? "font-semibold" : ""} ${row.highlight ? "text-primary font-bold" : "text-foreground"}`}
                      >
                        {row.label}
                      </td>
                      <td className="px-4 py-2.5 text-right text-muted-foreground text-xs w-16">
                        {row.pct}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums w-32 ${row.bold ? "font-semibold" : ""} ${row.highlight ? "text-primary font-bold" : "text-foreground"}`}
                      >
                        {formatMKD(row.value)}
                      </td>
                      <td className="px-2 py-2.5 text-right text-xs text-muted-foreground w-10">
                        MKD
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground">
                {t("payroll.adjustments")} ({adjustments.length})
              </p>
              {isDraft && !showAdjForm && !readOnly && (
                <button
                  onClick={() => setShowAdjForm(true)}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t("payroll.addAdjustment")}
                </button>
              )}
            </div>

            {adjustments.length > 0 ? (
              <div className="space-y-1.5">
                {adjustments.map((adj) => {
                  const adjCfg = ADJ_TYPES.find((a) => a.value === adj.type) || ADJ_TYPES[4];
                  const AdjIcon = adjCfg.icon;
                  const isPositive = adj.type === "bonus" || adj.type === "on_call";
                  return (
                    <div
                      key={adj.id}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-muted/50 text-sm"
                    >
                      <AdjIcon className={`h-4 w-4 ${adjCfg.color} shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{adj.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {t(adjCfg.labelKey)} &bull;{" "}
                          {adj.applyOn === "gross" ? t("payroll.onGross") : t("payroll.onNet")}
                        </p>
                      </div>
                      <span className={`font-semibold tabular-nums ${isPositive ? "text-emerald-600" : "text-red-600"}`}>
                        {isPositive ? "+" : "-"}{formatMKD(adj.amount)}
                      </span>
                      {isDraft && !readOnly && (
                        <button
                          onClick={() => onDeleteAdjustment(entry.id, adj.id)}
                          className="h-6 w-6 rounded flex items-center justify-center hover:bg-red-100 transition-colors shrink-0"
                        >
                          <X className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-4 text-center">
                {t("payroll.noAdjustments")}
              </p>
            )}

            {isDraft && showAdjForm && (
              <div className="mt-3 p-3 rounded-lg border border-primary/20 bg-card space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      {t("payroll.adjType")}
                    </label>
                    <select
                      value={adjForm.type}
                      onChange={(e) => setAdjForm({ ...adjForm, type: e.target.value })}
                      className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    >
                      {ADJ_TYPES.map((at) => (
                        <option key={at.value} value={at.value}>{t(at.labelKey)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      {t("payroll.applyOn")}
                    </label>
                    <select
                      value={adjForm.applyOn}
                      onChange={(e) => setAdjForm({ ...adjForm, applyOn: e.target.value })}
                      className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    >
                      <option value="gross">{t("payroll.onGross")}</option>
                      <option value="net">{t("payroll.onNet")}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    {t("payroll.adjName")}
                  </label>
                  <input
                    type="text"
                    value={adjForm.name}
                    onChange={(e) => setAdjForm({ ...adjForm, name: e.target.value })}
                    placeholder={t("payroll.adjNamePlaceholder")}
                    className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      {t("payroll.amount")}
                    </label>
                    <input
                      type="number"
                      value={adjForm.amount}
                      onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })}
                      placeholder="0"
                      className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm tabular-nums"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      {t("payroll.notes")}
                    </label>
                    <input
                      type="text"
                      value={adjForm.notes}
                      onChange={(e) => setAdjForm({ ...adjForm, notes: e.target.value })}
                      placeholder={t("payroll.notesPlaceholder")}
                      className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowAdjForm(false);
                      setAdjForm({ type: "bonus", name: "", amount: "", applyOn: "gross", notes: "" });
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    size="sm"
                    disabled={!adjForm.name || !adjForm.amount || parseInt(adjForm.amount) <= 0}
                    onClick={handleAddAdj}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    {t("payroll.addAdjustment")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Adjustments summary — show live preview or saved values */}
          {(hasAdj || salaryPreview) && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("payroll.gross")} ({t("payroll.base")})
                </span>
                <span className="tabular-nums">{formatMKD(entry.grossSalary)} MKD</span>
              </div>
              {((salaryPreview?.grossAdj ?? entry.totalAdjustmentsGross) !== 0) && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("payroll.grossAdj")}</span>
                  <span
                    className={`tabular-nums ${(salaryPreview?.grossAdj ?? entry.totalAdjustmentsGross) > 0 ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {(salaryPreview?.grossAdj ?? entry.totalAdjustmentsGross) > 0 ? "+" : ""}
                    {formatMKD(Math.abs(salaryPreview?.grossAdj ?? entry.totalAdjustmentsGross))} MKD
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("payroll.net")} ({t("payroll.calculated")})
                </span>
                <span className="tabular-nums">
                  {formatMKD(displayBreakdown?.netSalary ?? entry.netSalary)} MKD
                </span>
              </div>
              {((salaryPreview?.netAdj ?? entry.totalAdjustmentsNet) !== 0) && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("payroll.netAdj")}</span>
                  <span
                    className={`tabular-nums ${(salaryPreview?.netAdj ?? entry.totalAdjustmentsNet) > 0 ? "text-emerald-600" : "text-red-600"}`}
                  >
                    {(salaryPreview?.netAdj ?? entry.totalAdjustmentsNet) > 0 ? "+" : ""}
                    {formatMKD(Math.abs(salaryPreview?.netAdj ?? entry.totalAdjustmentsNet))} MKD
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center p-3 rounded-xl bg-primary/5 mt-2">
                <span className="font-bold text-primary">
                  {t("payroll.finalNet")}
                  {salaryPreview && <span className="ml-1 text-xs font-normal">({t("payroll.preview")})</span>}
                </span>
                <span className="text-lg font-bold text-primary tabular-nums">
                  {formatMKD(displayNet)} MKD
                </span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { EntrySheet };
